/**
 * usePrescreenAttempt — the shared attempt lifecycle + optimistic answer-save
 * plumbing behind every live prescreen surface (the one-question-at-a-time
 * PrescreenRunner and the all-visible PrescreenAllPane in Call Mode).
 *
 * Extracted verbatim from PrescreenRunner so there is ONE save path: answers
 * save the moment they change; failed saves queue and retry on the next action
 * (flaky LTE must never stall a live call); in-flight promises are awaited before
 * terminal actions; `latestValue` guards a stale retry from overwriting a newer
 * answer for the same question. Components own only their own UI (navigation,
 * keyboard, summary/footer) — never a second copy of this logic.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';
import { Prescreen, PrescreenCriterion, StudyAttempt } from '../../types';
import { answerValue, evalCriterionRule, resolveCalculations, RuleVerdict } from '../../lib';

export type AttemptPhase = 'starting' | 'ready' | 'no_prescreen' | 'error';

export interface PrescreenAttemptApi {
  phase: AttemptPhase;
  errorMsg: string | null;
  setErrorMsg: (m: string | null) => void;
  prescreen: Prescreen | null;
  criteria: PrescreenCriterion[];
  attempt: StudyAttempt | null;
  answers: Record<string, any>;
  /** answers + computed calculation values (age/BMI), keyed by question_key. */
  resolvedAnswers: Record<string, any>;
  /** First unanswered index on load (the runner jumps here on a resumed run). */
  initialIdx: number;
  setAnswer: (criterion: PrescreenCriterion, value: any) => void;
  verdictFor: (c: PrescreenCriterion) => RuleVerdict;
  /** Drain queued retries + await in-flight saves — call before any terminal action. */
  flushPending: () => Promise<void>;
}

export function usePrescreenAttempt(contactId: string, studyId: string): PrescreenAttemptApi {
  const [phase, setPhase] = useState<AttemptPhase>('starting');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<StudyAttempt | null>(null);
  const [prescreen, setPrescreen] = useState<Prescreen | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [initialIdx, setInitialIdx] = useState(0);
  // Save plumbing: failed saves queue for retry; in-flight promises are awaited
  // before terminal actions; latestValue guards against a stale retry
  // overwriting a newer answer for the same question.
  const retryQueue = useRef<Map<string, { answerType: string; value: any }>>(new Map());
  const inFlight = useRef<Map<string, Promise<unknown>>>(new Map());
  const latestValue = useRef<Map<string, any>>(new Map());

  const criteria = useMemo(() => prescreen?.criteria || [], [prescreen]);

  // Load the published prescreen FIRST — only start an attempt when there is
  // one (otherwise an open zero-question attempt would brick the launcher).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const psRes = await api.get(`/api/crm/studies/${studyId}/prescreen`);
        if (!alive) return;
        const ps: Prescreen | null = psRes.data?.prescreen || null;
        if (!ps || !ps.criteria?.length) {
          setPhase('no_prescreen');
          return;
        }
        const startRes = await api.post(`/api/crm/contacts/${contactId}/attempts`, { study_id: studyId });
        if (!alive) return;
        const att = startRes.data?.attempt;
        setPrescreen(ps);
        setAttempt(att);

        // Resumed run: seed saved answers and jump to the first unanswered.
        const seeded: Record<string, any> = {};
        if (startRes.data?.resumed) {
          try {
            const histRes = await api.get(`/api/crm/contacts/${contactId}/attempts`);
            const mine = (histRes.data?.attempts || []).find((a: StudyAttempt) => a.id === att.id);
            for (const ans of mine?.answers || []) seeded[ans.question_key] = answerValue(ans);
          } catch { /* start fresh-looking; saves still upsert */ }
        }
        if (!alive) return;
        setAnswers(seeded);
        // Calc rows aren't answered directly — skip them when picking where to resume.
        const firstUnanswered = ps.criteria.findIndex((c) => !c.calculation && seeded[c.question_key] === undefined);
        setInitialIdx(firstUnanswered === -1 ? ps.criteria.length - 1 : firstUnanswered);
        setPhase('ready');
      } catch (err: any) {
        if (!alive) return;
        setErrorMsg(err?.response?.data?.error || 'Could not start the prescreen');
        setPhase('error');
      }
    })();
    return () => { alive = false; };
  }, [contactId, studyId]);

  /** POST one answer; queue retryable failures, drop 4xx (permanent), and
   *  never let a stale request clear/overwrite state for a newer value. */
  const postAnswer = useCallback((attemptId: string, key: string, answerType: string, value: any) => {
    const p = api.post(`/api/crm/attempts/${attemptId}/answers`, { question_key: key, answer_type: answerType, value })
      .then(() => {
        const q = retryQueue.current.get(key);
        if (q && q.value === value) retryQueue.current.delete(key);
      })
      .catch((err) => {
        if (latestValue.current.get(key) !== value) return; // superseded — drop
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) return; // permanent — don't loop
        retryQueue.current.set(key, { answerType, value });
      })
      .finally(() => {
        if (inFlight.current.get(key) === p) inFlight.current.delete(key);
      });
    inFlight.current.set(key, p);
    return p;
  }, []);

  const persistAnswer = useCallback((criterion: PrescreenCriterion, value: any) => {
    if (!attempt) return;
    latestValue.current.set(criterion.question_key, value);
    retryQueue.current.delete(criterion.question_key);
    // Flush OTHER queued keys (the current key was just superseded above).
    for (const [key, item] of [...retryQueue.current.entries()]) {
      retryQueue.current.delete(key);
      postAnswer(attempt.id, key, item.answerType, item.value);
    }
    postAnswer(attempt.id, criterion.question_key, criterion.answer_type, value);
  }, [attempt, postAnswer]);

  const flushPending = useCallback(async () => {
    if (!attempt) return;
    for (const [key, item] of [...retryQueue.current.entries()]) {
      retryQueue.current.delete(key);
      postAnswer(attempt.id, key, item.answerType, item.value);
    }
    await Promise.allSettled([...inFlight.current.values()]);
  }, [attempt, postAnswer]);

  const setAnswer = useCallback((criterion: PrescreenCriterion, value: any) => {
    setAnswers((a) => ({ ...a, [criterion.question_key]: value }));
    persistAnswer(criterion, value);
  }, [persistAnswer]);

  // Fill in computed calculation values (age/BMI) before evaluating verdicts.
  const resolvedAnswers = useMemo(() => resolveCalculations(criteria, answers), [criteria, answers]);

  const verdictFor = useCallback((c: PrescreenCriterion): RuleVerdict => {
    const v = resolvedAnswers[c.question_key];
    if (v === undefined) return 'unknown';
    return evalCriterionRule(c.rule, v);
  }, [resolvedAnswers]);

  return {
    phase, errorMsg, setErrorMsg, prescreen, criteria, attempt, answers, resolvedAnswers,
    initialIdx, setAnswer, verdictFor, flushPending,
  };
}

export default usePrescreenAttempt;
